import type { ImageCrop } from '../types';

export async function createImageObjectUrl(file: File) {
  if (!isSvgFile(file)) {
    return URL.createObjectURL(file);
  }

  const svgMarkup = await file.text();
  const normalizedSvg = normalizeSvgDimensions(svgMarkup);
  return URL.createObjectURL(new Blob([normalizedSvg], { type: 'image/svg+xml' }));
}

export function isSvgFile(file: File) {
  return file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
}

export function isImageFile(file: File) {
  return file.type.startsWith('image/') || /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(file.name);
}

function normalizeSvgDimensions(svgMarkup: string) {
  const parser = new DOMParser();
  const svgDocument = parser.parseFromString(svgMarkup, 'image/svg+xml');
  const svg = svgDocument.documentElement;

  if (!svg || svg.tagName.toLowerCase() !== 'svg' || svgDocument.querySelector('parsererror')) {
    return svgMarkup;
  }

  const viewBoxDimensions = parseSvgViewBox(svg.getAttribute('viewBox'));
  if (!viewBoxDimensions) {
    return svgMarkup;
  }

  if (!svg.hasAttribute('width')) {
    svg.setAttribute('width', String(viewBoxDimensions.width));
  }
  if (!svg.hasAttribute('height')) {
    svg.setAttribute('height', String(viewBoxDimensions.height));
  }

  if (svgMarkup.includes('currentColor')) {
    ensureSvgCurrentColor(svg, '#fffaf2');
  }

  return new XMLSerializer().serializeToString(svgDocument);
}

function ensureSvgCurrentColor(svg: Element, color: string) {
  const style = svg.getAttribute('style') ?? '';
  const hasStyleColor = /(^|;)\s*color\s*:/i.test(style);
  if (!svg.hasAttribute('color') && !hasStyleColor) {
    svg.setAttribute('color', color);
  }
}

function parseSvgViewBox(viewBox: string | null) {
  const parts = viewBox?.trim().split(/[\s,]+/).map(Number) ?? [];
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part)) || parts[2] <= 0 || parts[3] <= 0) {
    return null;
  }

  return {
    width: parts[2],
    height: parts[3],
  };
}

export function detectVisibleImageCrop(image: HTMLImageElement): ImageCrop | null {
  try {
    return measureVisibleImageCrop(image);
  } catch {
    return null;
  }
}

function measureVisibleImageCrop(image: HTMLImageElement): ImageCrop | null {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) {
    return null;
  }

  const maxSampleSize = 512;
  const sampleScale = Math.min(1, maxSampleSize / Math.max(sourceWidth, sourceHeight));
  const sampleWidth = Math.max(1, Math.round(sourceWidth * sampleScale));
  const sampleHeight = Math.max(1, Math.round(sourceHeight * sampleScale));
  const sampleCanvas = document.createElement('canvas');
  sampleCanvas.width = sampleWidth;
  sampleCanvas.height = sampleHeight;

  const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
  if (!sampleContext) {
    return null;
  }

  sampleContext.clearRect(0, 0, sampleWidth, sampleHeight);
  sampleContext.drawImage(image, 0, 0, sampleWidth, sampleHeight);

  const pixels = sampleContext.getImageData(0, 0, sampleWidth, sampleHeight).data;
  let minX = sampleWidth;
  let minY = sampleHeight;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < sampleHeight; y += 1) {
    for (let x = 0; x < sampleWidth; x += 1) {
      const alpha = pixels[(y * sampleWidth + x) * 4 + 3];
      if (alpha > 8) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) {
    return null;
  }

  const padding = Math.ceil(Math.min(sampleWidth, sampleHeight) * 0.015);
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(sampleWidth - 1, maxX + padding);
  maxY = Math.min(sampleHeight - 1, maxY + padding);

  const crop = {
    x: minX / sampleScale,
    y: minY / sampleScale,
    width: (maxX - minX + 1) / sampleScale,
    height: (maxY - minY + 1) / sampleScale,
  };

  const cropRatio = (crop.width * crop.height) / (sourceWidth * sourceHeight);
  return cropRatio > 0.985 ? null : crop;
}
